import { html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PdpProductBrand } from 'components/discovery/pdp/typing/pdp-product.js'


export const fontVariants = ['caption-s', 'caption-m', 'caption-l'] as const
export type fontVariantsType = typeof fontVariants

export const brandDataSources = ['attributes', 'pdp-product'] as const
export type BrandDataSource = typeof brandDataSources[number]

export const brandLayouts = ['default', 'image'] as const
export type BrandLayout = typeof brandLayouts[number]

@customElement('csc-brand')
export class CSCBrand extends LitElement {

  @property({ type: String })
  layout: BrandLayout = 'default'

  /** @deprecated Use `layout` instead. */
  @property({ type: String, attribute: 'variant' })
  set variant(value: BrandLayout) {
    console.warn('<csc-brand> "variant" is deprecated, use "layout" instead.')
    this.layout = value
  }

  get variant() {
    return this.layout
  }

  @property({ type: String })
  text: string = 'Brand'

  @property({ type: String, attribute: 'font-variant' })
  fontVariant: fontVariantsType[number] = 'caption-s'

  @property({ type: String, attribute: 'image-source' })
  imageSource = ''

  @property({ type: String })
  alt = 'Brand name'

  @property({ type: Number })
  width: number | null = null

  @property({ type: Number })
  height: number | null = null

  @property({ type: String })
  url: string = ''

  @property({ type: String, attribute: 'data-source' })
  dataSource: BrandDataSource = 'attributes'

  @consume({ context: productTileProductContext })
  @state()
  private _tileProduct: InternalProduct | undefined = undefined

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @state()
  private pdpBrand: PdpProductBrand | null = null

  @state()
  private pdpBrandUrl = ''

  constructor() {
    super()
    createEffect(() => {
      const brand = this.store.product()?.brand
      if (brand) void this.setPdpBrand(brand)
    })
  }

  private async setPdpBrand(brand: PdpProductBrand) {
    this.pdpBrand = brand
    this.pdpBrandUrl = ''
    if (!brand.slug) return
    const url = (await createUrl(`${brand.slug}/`)).href
    if (this.pdpBrand === brand) this.pdpBrandUrl = url
  }

  private get usesPdpBrand(): boolean {
    return !this._tileProduct && this.dataSource === 'pdp-product'
  }

  override render() {
    if (this.usesPdpBrand) {
      const brand = this.pdpBrand
      if (!brand?.name) return nothing
      if (this.layout === 'image' && !brand.picture) return nothing
      return this.renderBrand(brand.name, brand.picture ?? '', brand.name, this.pdpBrandUrl)
    }

    return this.renderBrand(
      this._tileProduct?.brandName ?? this.text,
      this._tileProduct?.brandImage || this.imageSource,
      this._tileProduct?.brandName || this.alt,
      this.url,
    )
  }

  private renderBrand(effectiveText: string, effectiveImageSource: string, effectiveAlt: string, url: string) {
    if (this.layout === 'image' && effectiveImageSource) {
      const imageHtml = html`
        <cse-image
          source="${effectiveImageSource}"
          alt="${effectiveAlt}"
          width="${this.width ?? nothing}"
          height="${this.height ?? nothing}"
        ></cse-image>
      `

      return html`
        ${url
          ? html`
            <a href="${url}">
              ${imageHtml}
            </a>
          `
          : imageHtml
        }
      `
    } else {
      return html`
        ${url
          ? html`
            <cse-link url="${url}" font-variant="${this.fontVariant}">
              ${effectiveText}
            </cse-link>
          `
          : html`
            <cse-text variant="${this.fontVariant}">
              ${effectiveText}
            </cse-text>
          `
        }
      `
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-brand': CSCBrand
  }
}
